import { clsx } from 'clsx';

import { Badge } from '@/vibes/soul/primitives/badge';
import { Price, PriceLabel } from '@/vibes/soul/primitives/price-label';
import * as Skeleton from '@/vibes/soul/primitives/skeleton';
import { Image } from '~/components/image';
import { Link } from '~/components/link';

export interface Product {
  id: string;
  title: string;
  href: string;
  image?: { src: string; alt: string };
  price?: Price;
  subtitle?: string;
  badge?: string;
  inventoryMessage?: string;
}

export interface ProductCardProps {
  className?: string;
  colorScheme?: 'light' | 'dark';
  aspectRatio?: '5:6' | '3:4' | '1:1';
  imagePriority?: boolean;
  imageSizes?: string;
  product: Product;
}

/**
 * This component supports various CSS variables for theming. Here's a comprehensive list, along
 * with their default values:
 *
 * ```css
 * :root {
 *   --product-card-focus: hsl(var(--primary));
 *   --product-card-light-offset: hsl(var(--background));
 *   --product-card-light-background: hsl(var(--contrast-100));
 *   --product-card-light-title: hsl(var(--foreground));
 *   --product-card-light-subtitle: hsl(var(--foreground) / 75%);
 *   --product-card-light-message: hsl(var(--foreground) / 75%);
 *   --product-card-dark-offset: hsl(var(--foreground));
 *   --product-card-dark-background: hsl(var(--contrast-500));
 *   --product-card-dark-title: hsl(var(--background));
 *   --product-card-dark-subtitle: hsl(var(--background) / 75%);
 *   --product-card-dark-message: hsl(var(--background) / 75%);
 *   --product-card-font-family: var(--font-family-body);
 * }
 * ```
 */
export function ProductCard({
  product: { id, title, badge, price, image, href, inventoryMessage },
  colorScheme = 'light',
  className,
  aspectRatio = '5:6',
  imagePriority = false,
  imageSizes = '(min-width: 80rem) 20vw, (min-width: 64rem) 25vw, (min-width: 42rem) 33vw, (min-width: 24rem) 50vw, 100vw',
}: ProductCardProps) {
  return (
    <article
      className={clsx(
        'group flex min-w-0 max-w-md flex-col gap-3 font-[family-name:var(--card-font-family,var(--font-family-body))] @container',
        className,
      )}
    >
      <div className="relative">
        <div
          className={clsx(
            'relative overflow-hidden rounded-xl @md:rounded-2xl',
            {
              '5:6': 'aspect-5/6',
              '3:4': 'aspect-3/4',
              '1:1': 'aspect-square',
            }[aspectRatio],
            {
              light: 'bg-(--product-card-light-background,hsl(var(--contrast-100)))',
              dark: 'bg-(--product-card-dark-background,hsl(var(--contrast-500)))',
            }[colorScheme],
          )}
        >
          {image != null ? (
            <Image
              alt={image.alt}
              className={clsx(
                'w-full scale-100 select-none object-cover transition-transform duration-500 ease-out group-hover:scale-110',
                {
                  light: 'bg-[var(--product-card-light-background,hsl(var(--contrast-100))]',
                  dark: 'bg-[var(--product-card-dark-background,hsl(var(--contrast-500))]',
                }[colorScheme],
              )}
              fill
              fetchPriority={imagePriority ? 'high' : undefined}
              priority={imagePriority}
              preset="product-card"
              sizes={imageSizes}
              src={image.src}
            />
          ) : (
            <div
              className={clsx(
                'wrap-break-word pl-5 pt-5 text-4xl font-bold leading-[0.8] tracking-tighter opacity-25 transition-transform duration-500 ease-out group-hover:scale-105 @xs:text-7xl',
                {
                  light: 'text-(--product-card-light-title,hsl(var(--foreground)))',
                  dark: 'text-(--product-card-dark-title,hsl(var(--background)))',
                }[colorScheme],
              )}
            >
              {title}
            </div>
          )}
          {badge != null && badge !== '' && (
            <Badge className="absolute left-3 top-3">{badge}</Badge>
          )}
        </div>

        <div className="mt-2 flex flex-col items-start gap-x-4 gap-y-3 px-1 @xs:mt-3 @2xl:flex-row">
          <div className="flex-1 text-sm @[16rem]:text-base">
            <span
              className={clsx(
                'line-clamp-2 font-light',
                {
                  light: 'text-(--product-card-light-title,hsl(var(--foreground)))',
                  dark: 'text-(--product-card-dark-title,hsl(var(--background)))',
                }[colorScheme],
              )}
            >
              {title}
            </span>
            {price != null && <PriceLabel className="mt-1.5" colorScheme={colorScheme} price={price} />}
            <span
              className={clsx(
                'block text-sm font-normal',
                {
                  light: 'text-(--product-card-light-message,hsl(var(--foreground)/75%))',
                  dark: 'text-(--product-card-dark-message,hsl(var(--background)/75%))',
                }[colorScheme],
              )}
            >
              {inventoryMessage}
            </span>
          </div>
        </div>
        {href !== '#' && (
          <Link
            className={clsx(
              'absolute inset-0 rounded-b-lg rounded-t-2xl focus:outline-hidden focus-visible:ring-2 focus-visible:ring-(--product-card-focus,hsl(var(--primary))) focus-visible:ring-offset-4',
              {
                light: 'ring-offset-(--product-card-light-offset,hsl(var(--background)))',
                dark: 'ring-offset-(--product-card-dark-offset,hsl(var(--foreground)))',
              }[colorScheme],
            )}
            href={href}
            id={id}
          >
            <span className="sr-only">{title}</span>
          </Link>
        )}
      </div>
    </article>
  );
}

export function ProductCardSkeleton({
  className,
  aspectRatio = '5:6',
}: {
  aspectRatio?: '5:6' | '3:4' | '1:1';
  className?: string;
}) {
  return (
    <div className={clsx('@container', className)}>
      <Skeleton.Box
        className={clsx(
          'rounded-xl @md:rounded-2xl',
          {
            '5:6': 'aspect-5/6',
            '3:4': 'aspect-3/4',
            '1:1': 'aspect-square',
          }[aspectRatio],
        )}
      />
      <div className="mt-2 flex flex-col items-start gap-x-4 gap-y-3 px-1 @xs:mt-3 @2xl:flex-row">
        <div className="w-full text-sm @[16rem]:text-base">
          <Skeleton.Text characterCount={10} className="rounded" />
          <Skeleton.Text characterCount={8} className="rounded" />
          <Skeleton.Text characterCount={6} className="rounded" />
        </div>
      </div>
    </div>
  );
}
